import i18n, { type BackendModule, type ReadCallback } from 'i18next';
import { initReactI18next } from 'react-i18next';
import pl from './pl.json';
import { detectInitialLanguageFromEnv, kodBcp47 } from '@/utils/language';

/**
 * TYLKO POLSKI W PACZCE STARTOWEJ (30.09). Cztery słowniki importowane statycznie to było
 * ~1 MB JSON-a i 280 KB gzip, czyli 36% startowego JS-u produkcji (zmierzone), a strona potrzebuje
 * jednego języka. Polski zostaje statycznie (domyślny, większość ruchu — zero dodatkowych
 * zapytań), en/ua/by dociąga ten backend dynamicznym importem, gdy język jest wybrany albo
 * potrzebny jako zapas. i18next wstrzymuje `languageChanged`, aż zestaw przyjdzie, więc przy
 * zmianie języka w trakcie sesji nie widać kluczy; na starcie render czeka na `i18nGotowe`.
 */
const LADOWANIE: Record<string, () => Promise<{ default: unknown }>> = {
  en: () => import('./en.json'),
  ua: () => import('./ua.json'),
  by: () => import('./by.json'),
};
const slowniki: BackendModule = {
  type: 'backend',
  init() {},
  read(language: string, _namespace: string, callback: ReadCallback) {
    const laduj = LADOWANIE[language];
    // pl jest w paczce (`resources`), inne kody nie istnieją — pusty zestaw, bez błędu.
    if (!laduj) { callback(null, {}); return; }
    laduj().then((m) => callback(null, m.default as never), (e) => callback(e as Error, false));
  },
};

/**
 * `<html lang>` W JĘZYKU INTERFEJSU (30.09). Do dziś `index.html` miał na sztywno `lang="en"` i nic
 * go nie zmieniało, więc czytnik ekranu czytał polski interfejs angielską wymową, a przeglądarka
 * proponowała tłumaczenie „z angielskiego”. Teraz `lang` idzie za językiem i18next: od razu przy
 * starcie (język znany przed wczytaniem słowników) i przy każdej zmianie. Kod BCP-47 — `uk`/`be`
 * dla naszych `ua`/`by` (`kodBcp47`).
 */
export function ustawJezykDokumentu(lng: string | null | undefined): void {
  if (typeof document !== 'undefined') document.documentElement.lang = kodBcp47(lng);
}
const jezykStartowy = detectInitialLanguageFromEnv();
ustawJezykDokumentu(jezykStartowy);
i18n.on('languageChanged', ustawJezykDokumentu);

export const i18nGotowe = i18n.use(slowniki).use(initReactI18next).init({
  resources: { pl: { translation: pl } },
  // Zasoby częściowo w paczce, reszta z backendu.
  partialBundledLanguages: true,
  // Default to a stored choice, else the browser/system language, else Polish.
  lng: jezykStartowy,
  // ZAPAS BEZ ANGIELSKIEGO NA STARCIE DLA POLSKIEGO. Dawniej `'en'` dla wszystkich — przy leniwych
  // słownikach ściągałoby angielski każdemu polskiemu użytkownikowi. Polski ma KAŻDY klucz
  // angielskiego (src/i18n/kompletnosc.test.ts), więc zapasu nie potrzebuje. ua/by to szkice
  // z pustymi wartościami — zapas angielski jak dotąd, potem polski; angielski — polski.
  fallbackLng: { ua: ['en', 'pl'], by: ['en', 'pl'], en: ['pl'], default: ['pl'] },
  // The ua/by files are stubs (repo convention) and carry MANY keys whose value
  // is "" — a deliberate placeholder. i18next's default is to treat an empty
  // string as a valid translation, so those keys rendered as literally NOTHING:
  // a multi-language sweep at 360px showed the lease and furnishing pills as
  // blank capsules in Ukrainian and Belarusian. Falling back on empty makes a
  // stub render English instead of vanishing — always better than an empty
  // control, and it removes the failure mode for every future stubbed key.
  returnEmptyString: false,
  interpolation: {
    escapeValue: false,
  },
});

/**
 * LICZBA MNOGA DLA `ua` I `by` (znalezione przy przeglądzie tłumaczeń 28.09). Nasze kody języków
 * to `ua` i `by`, a nie `uk` i `be` — `Intl.PluralRules` ich nie zna i bierze język SYSTEMU.
 * Zmierzone na i18next 26: przy angielskim systemie 2 i 5 dawały formę `_other` („5 фільтра"
 * zamiast „5 фільтрів"), przy polskim 21 dawało `_many` („21 фільтрів" zamiast „21 фільтр").
 * Kody zostają (adresy, zapisany wybór języka, nazwy plików); podmieniamy tylko regułę liczby
 * mnogiej. Pilnuje tego src/i18n/liczbaMnogaUaBy.test.ts.
 */
const resolver = i18n.services.pluralResolver as unknown as {
  getRule: (code: string, options?: Record<string, unknown>) => unknown;
};
const regulaOryginalna = resolver.getRule.bind(resolver);
// To samo mapowanie co `<html lang>` (`kodBcp47`); kody spoza naszych czterech przechodzą bez zmian.
resolver.getRule = (code, options) => regulaOryginalna(code === 'ua' || code === 'by' ? kodBcp47(code) : code, options);

export default i18n;
